/**
 * Plain-English research-area PRESENTATION layer.
 *
 * The site's operational catalog is organised by chemistry class
 * (growth-hormone secretagogues, GLP analogs, mitochondrial peptides,
 * …).  For the average shopper those labels are opaque.  This module
 * defines nine familiar research areas that surface the SAME 106 SKUs
 * without renaming anything server-side.
 *
 * Rules
 * -----
 *   1. Presentation ONLY — the backend `category` slug on every product
 *      is untouched; product names, prices, and SKUs are byte-identical.
 *   2. A product MAY belong to more than one area (e.g. BPC-157 shows
 *      under Recovery, and Melanotan-2 shows under both Skin & Hair
 *      and Hormone & Sexual Wellness).
 *   3. Every sellable product remains reachable via at least ONE area
 *      thanks to the `other-research-compounds` catch-all bucket.
 *   4. Descriptions are honest ("commonly studied in X research") —
 *      NEVER "treats X" or "for X therapy".  Wording caps at ~16 words.
 *   5. Filters are matched against `category_slug` + `slug` prefix +
 *      keyword lists — never against invented tags.  A product only
 *      appears in the catch-all when no other area claims it.
 */

// --- 9 areas in the exact order the design brief specified ---
export const RESEARCH_AREAS = [
  {
    id:        "weight-management",
    label:     "Weight Management",
    testid:    "area-weight-management",
    description: "Compounds commonly studied in weight-management research, including GLP-family analogs.",
    examples:  "GLP-1, Cagrilintide, AOD-9604",
    category_slugs: ["glp-family-analogs"],
    slug_prefixes:  ["aod-9604", "adipotide", "tesofensine"],
  },
  {
    id:        "recovery-repair",
    label:     "Recovery & Repair",
    testid:    "area-recovery-repair",
    description: "Reference standards commonly studied in tissue-repair and recovery research.",
    examples:  "BPC-157, TB-500, Wolverine blend",
    category_slugs: [],
    slug_prefixes:  ["bpc-157", "tb-500", "bpc-157-tb-500", "kpv", "ll-37"],
  },
  {
    id:        "growth-performance",
    label:     "Growth & Performance",
    testid:    "area-growth-performance",
    description: "Growth-hormone secretagogue references commonly studied in performance research.",
    examples:  "CJC-1295, Ipamorelin, Sermorelin",
    category_slugs: ["growth-hormone-secretagogues"],
    slug_prefixes:  ["igf-1", "tesamorelin", "hexarelin", "mk-677"],
  },
  {
    id:        "healthy-aging-longevity",
    label:     "Healthy Aging & Longevity",
    testid:    "area-healthy-aging-longevity",
    description: "Bioregulator and mitochondrial peptide references commonly studied in longevity research.",
    examples:  "Epithalon, MOTS-c, SS-31",
    category_slugs: ["bioregulator-peptides", "mitochondrial-peptides"],
    slug_prefixes:  ["glutathione", "foxo4-dri", "amino-1mq", "urolithin"],
  },
  {
    id:        "immune-support",
    label:     "Immune Support",
    testid:    "area-immune-support",
    description: "Thymic and immune peptide references commonly studied in immune-modulation research.",
    examples:  "Thymalin, Thymogen, Thymosin-α1",
    category_slugs: ["thymic-immune-peptides"],
    slug_prefixes:  ["thymalin", "thymogen", "thymosin-alpha", "thymopoetin"],
    // TB-500 lives under Thymic in the DB but presents best under Recovery.
    exclude_slug_prefixes: ["tb-500"],
  },
  {
    id:        "hormone-sexual-wellness",
    label:     "Hormone & Sexual Wellness",
    testid:    "area-hormone-sexual-wellness",
    description: "References commonly studied in hormonal and sexual-wellness research contexts.",
    examples:  "PT-141, Kisspeptin, Teriparatide",
    category_slugs: [],
    slug_prefixes:  ["pt-141", "kisspeptin", "oxytocin", "teriparatide", "melanotan-2"],
  },
  {
    id:        "skin-hair",
    label:     "Skin & Hair",
    testid:    "area-skin-hair",
    description: "Copper-complex and pigmentation references commonly studied in skin and hair research.",
    examples:  "GHK-Cu, AHK-Cu, Melanotan-2",
    category_slugs: ["copper-complex-peptides"],
    slug_prefixes:  ["melanotan"],
  },
  {
    id:        "focus-cognitive-research",
    label:     "Focus & Cognitive Research",
    testid:    "area-focus-cognitive-research",
    description: "Neuropeptide references commonly studied in cognitive and focus research.",
    examples:  "Semax, Selank, Cerebrolysin",
    category_slugs: ["neuropeptides"],
    slug_prefixes:  ["dihexa", "methylene-blue", "pnc-27", "cerebrolysin", "semax", "selank"],
  },
  {
    id:        "other-research-compounds",
    label:     "Other Research Compounds",
    testid:    "area-other-research-compounds",
    description: "Investigational reference standards and companion diluent vials.",
    examples:  "Bacteriostatic Water, Acetic Acid Water",
    category_slugs: [],
    slug_prefixes:  [],
    // Catch-all — never matches directly.  `bucketProducts` fills this
    // with anything that no other area claimed, so every sellable
    // product remains reachable via at least one plain-English group.
    catch_all: true,
  },
];

// --- helpers ---

const _slug = (p) => (p?.slug || "").toLowerCase();

/** True if a product matches a specific research-area rule (excluding catch-all). */
export function productMatchesArea(product, area) {
  if (!product || !area || area.catch_all) return false;
  const slug = _slug(product);
  const cat  = (product.category_slug || product.category || "").toLowerCase();

  // Explicit exclusions win — TB-500 must not appear under Immune.
  if ((area.exclude_slug_prefixes || []).some((pref) => slug.startsWith(pref))) {
    return false;
  }

  if ((area.category_slugs || []).includes(cat)) return true;
  if ((area.slug_prefixes  || []).some((pref) => slug.startsWith(pref))) return true;
  return false;
}

/** Return the FIRST (primary) research area for a product, or the catch-all. */
export function primaryAreaFor(product) {
  for (const area of RESEARCH_AREAS) {
    if (productMatchesArea(product, area)) return area;
  }
  return RESEARCH_AREAS[RESEARCH_AREAS.length - 1]; // catch-all
}

/**
 * Group every product into one area bucket.  A product goes into the FIRST
 * area that claims it (definition order matters).  Anything that no
 * specific area claims falls into the catch-all so no product goes dark.
 */
export function bucketProducts(products) {
  const buckets = new Map(RESEARCH_AREAS.map((a) => [a.id, []]));
  const catchAllId = RESEARCH_AREAS.find((a) => a.catch_all)?.id;
  for (const p of (products || [])) {
    let claimed = false;
    for (const area of RESEARCH_AREAS) {
      if (area.catch_all) continue;
      if (productMatchesArea(p, area)) {
        buckets.get(area.id).push(p);
        claimed = true;
        break;
      }
    }
    if (!claimed && catchAllId) buckets.get(catchAllId).push(p);
  }
  return buckets;
}

/**
 * Returns every product tagged as belonging to a given area, honouring
 * multi-area membership so browsing does the right thing when the same
 * SKU is relevant to two different topics (Melanotan-2 belongs to both
 * Hormone & Sexual Wellness AND Skin & Hair).
 */
export function productsInArea(products, areaId) {
  const area = RESEARCH_AREAS.find((a) => a.id === areaId);
  if (!area) return [];
  if (area.catch_all) {
    // Members are whatever primary bucket dumped into catch-all.
    return bucketProducts(products || []).get(area.id) || [];
  }
  return (products || []).filter((p) => productMatchesArea(p, area));
}

/**
 * True if `product` looks like a companion diluent / accessory the shopper
 * is likely to want alongside a lyophilized peptide vial.
 * Used by the mini-cart / full-cart recommender to prefer honest
 * accessories over cross-category picks.
 */
export function isCompanionAccessory(product) {
  const s = _slug(product);
  return /^(?:bacteriostatic|acetic-acid)-|-water-\d+/i.test(s);
}
